import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./CountdownText.module.scss";

export interface CountdownTextHandle {
	startOtpResendCodeTimer: () => void;
	hideCountdownTextTimer: () => void;
}

export interface CountdownTextProps {
	timerDurationInSeconds?: number;
	startTimerOnInit?: boolean;
	mainText?: string;
	expiredMessage?: string;
	onResend?: () => void;
	timerTick?: () => void;
}

export const CountdownText = React.forwardRef<CountdownTextHandle, CountdownTextProps>(
	(
		{
			timerDurationInSeconds = 60,
			startTimerOnInit = false,
			mainText,
			expiredMessage,
			onResend,
			timerTick,
		},
		ref,
	) => {
		const [secondsLeft, setSecondsLeft] = useState(timerDurationInSeconds);
		const [hidden, setHidden] = useState(false);
		const intervalRef = useRef<ReturnType<typeof setInterval>>();

		const startTimer = () => {
			setHidden(false);
			setSecondsLeft(timerDurationInSeconds);
			clearInterval(intervalRef.current);
			intervalRef.current = setInterval(() => {
				setSecondsLeft(prev => {
					timerTick?.();
					if (prev <= 1) {
						clearInterval(intervalRef.current);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		};

		useImperativeHandle(ref, () => ({
			startOtpResendCodeTimer: startTimer,
			hideCountdownTextTimer: () => {
				setHidden(true);
				clearInterval(intervalRef.current);
			},
		}));

		useEffect(() => {
			if (startTimerOnInit) startTimer();
			return () => clearInterval(intervalRef.current);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		if (hidden) return null;

		const countdownLabel =
			secondsLeft <= 60
				? `Resend code in ${secondsLeft}s`
				: `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

		// Timer expired + onResend available → show "Didn't receive code? Resend code"
		if (secondsLeft === 0 && onResend) {
			return (
				<span className={styles.row}>
					<span className={styles.mainText}>{mainText ?? "Didn't receive code?"}</span>
					<button
						type="button"
						className={styles.resendBtn}
						onClick={() => {
							onResend();
							startTimer();
						}}
					>
						Resend code
					</button>
				</span>
			);
		}

		// Timer running OR expired without onResend
		return (
			<span className={styles.row}>
				{secondsLeft > 0 && (
					<span className={styles.mainText}>{mainText ?? "Didn't receive code?"}</span>
				)}
				<span className={styles.countdown}>
					{secondsLeft === 0 ? (expiredMessage ?? "Your OTP has expired.") : countdownLabel}
				</span>
			</span>
		);
	},
);

CountdownText.displayName = "CountdownText";
export default CountdownText;
